// ============================================================
//  Toast.jsx — Toast notification system
// ============================================================
import { createContext, useContext, useState, useCallback } from "react";
import { CheckCircle, XCircle, AlertCircle, X } from "lucide-react";

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  }, []);

  const remove = (id) => setToasts(t => t.filter(x => x.id !== id));

  const icons  = { success: CheckCircle, error: XCircle, warning: AlertCircle };
  const colors = {
    success: { bg: "#ECFDF5", border: "#6EE7B7", color: "#059669" },
    error:   { bg: "#FEF2F2", border: "#FECACA", color: "#DC2626" },
    warning: { bg: "#FFFBEB", border: "#FDE68A", color: "#D97706" },
  };

  return (
    <ToastContext.Provider value={{ toast: push }}>
      {children}
      <div style={{ position: "fixed", bottom: 20, right: 20, display: "flex", flexDirection: "column", gap: 8, zIndex: 9999 }}>
        {toasts.map(t => {
          const c = colors[t.type] || colors.success;
          const Icon = icons[t.type] || CheckCircle;
          return (
            <div key={t.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
              borderRadius: 10, borderLeft: `4px solid ${c.border}`,
              background: c.bg, boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              maxWidth: 360, minWidth: 260,
            }}>
              <Icon size={18} color={c.color} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: c.color }}>{t.msg}</span>
              <button onClick={() => remove(t.id)} style={{ background: "none", border: "none", cursor: "pointer", color: c.color, opacity: 0.5, padding: 2 }}>
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};

export default ToastContext;